import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell,
  TableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupIcon from "@mui/icons-material/Group";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableChartIcon from "@mui/icons-material/TableChart";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import * as XLSX from "xlsx";
import BackBtn from "../widgets/BackBtn";
import useApiEp from "../hooks/useApiEp";
import useEmailHeaderFooter from "../hooks/useEmailHeaderFooter";
import MuPb from "../widgets/MuPb";
import { getErrorResponse } from "../utils/util";

const TimeZoneIndia = { timeZone: "Asia/Kolkata" };

export function oesTemplate(
  user = {},
  templateBody = "",
  supplier = {},
  formTitle = "",
  headerUrl = "",
  footerUrl = ""
) {
  const headerImg = headerUrl
    ? `<img src="${headerUrl}" alt="Event Header" style="display: block; margin: 0 auto; width: 100%; border-radius: 8px; max-height: 180px; object-fit: contain;" />`
    : "";

  const footerImg = footerUrl
    ? `<img src="${footerUrl}" alt="Event Footer" style="display: block; margin: 20px auto 0 auto; width: 100%; border-radius: 8px; max-height: 140px; object-fit: contain;" />`
    : "";

  const supplierEmails = Array.isArray(supplier?.emails)
    ? supplier.emails.join(", ")
    : supplier?.email || supplier?.emails || "N/A";

  const companyName = user.companyName?.value || user.companyName || "Exhibitor Company";
  const contactName = user.contactName || `${user.contactFirstName?.value || user.firstName || ""} ${user.contactLastName?.value || user.lastName || ""}`.trim() || "Exhibitor Representative";
  const contactEmail = user.contactEmail?.value || user.contactEmail || user.email || "N/A";
  const contactPhone = user.mobile?.value || user.mobile || user.phone || "N/A";

  return `<!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>${formTitle || "OES Requisition Preview"}</title>
      <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f1f5f9; margin: 0; padding: 16px; color: #0f172a; }
          .container { max-width: 780px; margin: 0 auto; padding: 24px; background-color: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05); }
          .header-badge { display: inline-block; padding: 4px 12px; background-color: #dbeafe; color: #1e40af; border-radius: 20px; font-weight: 700; font-size: 0.8rem; text-transform: uppercase; margin-bottom: 12px; }
          .info-card { border: 1px solid #e2e8f0; padding: 14px 18px; margin: 16px 0; border-radius: 8px; background-color: #f8fafc; }
          .section-title { font-size: 1rem; font-weight: 700; color: #0f172a; margin-top: 20px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 2px solid #e2e8f0; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 14px; }
          th, td { padding: 10px 12px; text-align: left; border: 1px solid #e2e8f0; font-size: 0.875rem; }
          th { background-color: #f1f5f9; font-weight: 700; color: #334155; }
          .label-cell { width: 32%; font-weight: 600; background-color: #f8fafc; color: #475569; }
          .total-row { background-color: #f8fafc; font-weight: 700; font-size: 0.95rem; }
          .price-text { color: #15803d; font-weight: 700; }
      </style>
  </head>
  <body>
      <div class="container">
          ${headerImg}
          
          <div style="margin-top: 16px; display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 8px;">
            <div>
              <span class="header-badge">OES Form Requisition</span>
              <h2 style="margin: 0; font-size: 1.35rem; color: #0f172a;">${formTitle || "Online Exhibitor Service"}</h2>
            </div>
          </div>

          <!-- Exhibitor / Submitter Details -->
          <div class="info-card">
              <div style="font-weight: 700; color: #1e293b; margin-bottom: 8px; font-size: 0.95rem;">Exhibitor Information</div>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; font-size: 0.875rem;">
                <div><span style="color: #64748b;">Company:</span> <strong>${companyName}</strong></div>
                <div><span style="color: #64748b;">Contact Person:</span> ${contactName}</div>
                <div><span style="color: #64748b;">Email:</span> ${contactEmail}</div>
                <div><span style="color: #64748b;">Phone:</span> ${contactPhone}</div>
                ${user.stallNo ? `<div><span style="color: #64748b;">Stall No:</span> <strong>${user.stallNo}</strong></div>` : ""}
                ${user.hallNo ? `<div><span style="color: #64748b;">Hall No:</span> <strong>${user.hallNo}</strong></div>` : ""}
              </div>
          </div>

          <!-- Supplier / Vendor Details -->
          ${
            supplier?.supplierName && supplier.supplierName !== "N/A"
              ? `<div class="info-card">
                  <div style="font-weight: 700; color: #1e293b; margin-bottom: 8px; font-size: 0.95rem;">Assigned Vendor / Service Provider</div>
                  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; font-size: 0.875rem;">
                    <div><span style="color: #64748b;">Vendor:</span> <strong style="color: #2563eb;">${supplier.supplierName}</strong></div>
                    <div><span style="color: #64748b;">Phone:</span> ${supplier.phoneNumber || supplier.phone || "N/A"}</div>
                    <div><span style="color: #64748b;">Email:</span> ${supplierEmails}</div>
                    ${supplier.address ? `<div style="grid-column: 1 / -1;"><span style="color: #64748b;">Address:</span> ${supplier.address}</div>` : ""}
                  </div>
              </div>`
              : ""
          }

          <!-- Main Dynamic Body -->
          ${templateBody}

          ${footerImg}
      </div>
  </body>
  </html>`;
}

export const extractSubmissionMetrics = (item, idx = 0) => {
  if (!item) {
    return {
      _id: `tx-${idx}`,
      form: "OES Form",
      vendor: "N/A",
      exhibitor: "N/A",
      item: "OES Requisition",
      startDate: "N/A",
      endDate: "N/A",
      days: 1,
      qty: 1,
      rate: 0,
      amount: 0,
      itemsList: [],
      subtotal: 0,
      gst: 0,
      grandTotal: 0,
      rawSubmission: item,
    };
  }

  const formObj = typeof item.form === "object" && item.form !== null ? item.form : {};
  const formData =
    item.formData ||
    item.formValues ||
    item.values ||
    item.submissionData ||
    item.fieldsData ||
    item.data ||
    {};
  const supplierObj =
    item.selectedSupplier ||
    item.supplier ||
    item.supplierDetails ||
    {};

  const parseNum = (val) => {
    if (val === undefined || val === null || val === "") return null;
    if (typeof val === "number") return isNaN(val) ? null : val;
    if (typeof val === "string") {
      const clean = val.replace(/,/g, "").trim();
      const num = parseFloat(clean);
      return isNaN(num) ? null : num;
    }
    return null;
  };

  // 1. Locate items / products array if any
  let itemsList = [];
  if (Array.isArray(item.items) && item.items.length > 0) {
    itemsList = item.items;
  } else if (Array.isArray(item.products) && item.products.length > 0) {
    itemsList = item.products;
  } else if (Array.isArray(item.selectedProducts) && item.selectedProducts.length > 0) {
    itemsList = item.selectedProducts;
  } else if (Array.isArray(item.tableData) && item.tableData.length > 0) {
    itemsList = item.tableData;
  } else if (Array.isArray(item.ratedItems) && item.ratedItems.length > 0) {
    itemsList = item.ratedItems;
  } else if (Array.isArray(formData.items) && formData.items.length > 0) {
    itemsList = formData.items;
  } else if (Array.isArray(formData.products) && formData.products.length > 0) {
    itemsList = formData.products;
  } else if (Array.isArray(formData.selectedProducts) && formData.selectedProducts.length > 0) {
    itemsList = formData.selectedProducts;
  } else if (Array.isArray(formData.tableData) && formData.tableData.length > 0) {
    itemsList = formData.tableData;
  } else if (Array.isArray(formData.ratedItems) && formData.ratedItems.length > 0) {
    itemsList = formData.ratedItems;
  } else if (Array.isArray(formData.data) && formData.data.length > 0) {
    itemsList = formData.data;
  }

  // 2. Extract Grand Total / Amount from all possible keys
  let explicitGrandTotal =
    parseNum(item.grandTotal) ??
    parseNum(item.grand_total) ??
    parseNum(item.GrandTotal) ??
    parseNum(item["Grand Total"]) ??
    parseNum(formData.grandTotal) ??
    parseNum(formData.grand_total) ??
    parseNum(formData.GrandTotal) ??
    parseNum(formData["Grand Total"]) ??
    parseNum(formObj.grandTotal) ??
    null;

  let explicitTotal =
    parseNum(item.totalAmount) ??
    parseNum(item.total_amount) ??
    parseNum(item.TotalAmount) ??
    parseNum(item["Total Amount"]) ??
    parseNum(item.total) ??
    parseNum(item.Total) ??
    parseNum(item.amount) ??
    parseNum(item.Amount) ??
    parseNum(item.netAmount) ??
    parseNum(item.finalAmount) ??
    parseNum(item.subtotal) ??
    parseNum(item.subTotal) ??
    parseNum(formObj.amount) ??
    parseNum(formObj.total) ??
    parseNum(formData.totalAmount) ??
    parseNum(formData.total_amount) ??
    parseNum(formData.TotalAmount) ??
    parseNum(formData["Total Amount"]) ??
    parseNum(formData.total) ??
    parseNum(formData.Total) ??
    parseNum(formData.amount) ??
    parseNum(formData.Amount) ??
    parseNum(formData.subtotal) ??
    parseNum(formData.subTotal) ??
    null;

  let itemsSubtotal = 0;
  let totalQty = 0;
  let maxDays = 0;
  const itemNames = [];
  let minStartDate = null;
  let maxEndDate = null;

  const parsedItems = itemsList.map((row, rIdx) => {
    const rowName =
      row.description ||
      row.name ||
      row.item ||
      row.productName ||
      row.service ||
      row.services ||
      row.header ||
      `Item #${rIdx + 1}`;
    if (rowName) itemNames.push(rowName);

    const rQty = parseNum(row.qty) ?? parseNum(row.quantity) ?? parseNum(row.Quantity) ?? 1;
    const rRate = parseNum(row.rate) ?? parseNum(row.unitPrice) ?? parseNum(row.price) ?? parseNum(row.Rate) ?? 0;

    let rDays = parseNum(row.days) ?? parseNum(row.rentalDays) ?? parseNum(row.Days) ?? null;
    let fromDateStr = row.dateFrom || row.startDate || row.from;
    let toDateStr = row.dateTo || row.endDate || row.to;

    if (typeof row.date === "string") {
      if (row.date.includes("From:") && row.date.includes("To:")) {
        fromDateStr = row.date.split("From:")[1]?.split("To:")[0]?.trim();
        toDateStr = row.date.split("To:")[1]?.trim();
      } else if (row.date.includes("–") || row.date.includes("-")) {
        const parts = row.date.split(/[–-]/);
        if (parts.length === 2) {
          fromDateStr = parts[0]?.trim();
          toDateStr = parts[1]?.trim();
        }
      }
    }

    if (fromDateStr && !minStartDate) minStartDate = fromDateStr;
    if (toDateStr && !maxEndDate) maxEndDate = toDateStr;

    if (!rDays && fromDateStr && toDateStr) {
      try {
        const d1 = new Date(fromDateStr);
        const d2 = new Date(toDateStr);
        if (!isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
          const diffDays = Math.round(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
          rDays = diffDays > 0 ? diffDays : 1;
        }
      } catch {
        rDays = 1;
      }
    }

    const finalRowDays = rDays && rDays > 0 ? rDays : 1;
    maxDays = Math.max(maxDays, finalRowDays);
    totalQty += rQty;

    const rowTotal =
      parseNum(row.total) ??
      parseNum(row.Total) ??
      parseNum(row.amount) ??
      parseNum(row.Amount) ??
      parseNum(row.subtotal) ??
      parseNum(row.subTotal) ??
      rRate * rQty * finalRowDays;

    itemsSubtotal += rowTotal;

    return {
      name: rowName,
      specifications: row.specifications || row.specification || row.size || row["Size / Specification"] || "",
      qty: rQty,
      rate: rRate,
      days: finalRowDays,
      amount: rowTotal,
      fromDate: fromDateStr,
      toDate: toDateStr,
      rawRow: row,
    };
  });

  // Calculate Subtotal, GST, and Grand Total
  let subtotal = explicitTotal ?? (itemsSubtotal > 0 ? itemsSubtotal : 0);
  let explicitGst =
    parseNum(item.gst) ??
    parseNum(item.GST) ??
    parseNum(formData.gst) ??
    parseNum(formData.GST) ??
    parseNum(formData["18% GST"]) ??
    parseNum(formData.tax) ??
    null;

  let grandTotal = explicitGrandTotal;
  if (grandTotal === null || grandTotal === 0) {
    if (explicitGst !== null) {
      grandTotal = subtotal + explicitGst;
    } else if (subtotal > 0 && parsedItems.length > 0) {
      explicitGst = Math.round(subtotal * 0.18 * 100) / 100;
      grandTotal = Math.round((subtotal + explicitGst) * 100) / 100;
    } else {
      grandTotal = subtotal;
    }
  }

  if (explicitGst === null && grandTotal > subtotal && subtotal > 0) {
    explicitGst = Math.round((grandTotal - subtotal) * 100) / 100;
  }

  // Resolve Item Description
  let itemDesc =
    item.item ||
    formObj.item ||
    formObj.description ||
    item.formItem ||
    item.description ||
    formData.item ||
    formData.description;

  if (!itemDesc || itemDesc === item.name || itemDesc === "Additional Services" || itemDesc === "OES Form") {
    if (itemNames.length > 0) {
      itemDesc = itemNames.join(", ");
    } else {
      itemDesc = item.name || item.formName || formObj.name || formObj.title || "Additional Services";
    }
  }

  // Resolve Dates
  let startDateStr = item.startDate || formData.startDate || minStartDate || "N/A";
  let endDateStr = item.endDate || formData.endDate || maxEndDate || "N/A";

  if (startDateStr === "N/A" && item.createdOn) {
    startDateStr = new Date(item.createdOn).toLocaleDateString("en-GB");
  }

  const days = parseNum(item.days) ?? parseNum(formObj.days) ?? (maxDays > 0 ? maxDays : 1);
  const qty = parseNum(item.qty) ?? parseNum(formObj.qty) ?? (totalQty > 0 ? totalQty : 1);
  
  let rate = parseNum(item.rate) ?? parseNum(formObj.rate);
  if (rate === null || rate === 0) {
    if (subtotal > 0 && qty > 0 && days > 0) {
      rate = Math.round((subtotal / (qty * days)) * 100) / 100;
    } else if (grandTotal > 0 && qty > 0 && days > 0) {
      rate = Math.round((grandTotal / (qty * days)) * 100) / 100;
    } else {
      rate = 0;
    }
  }

  const formName = item.name || item.formName || formObj.name || formObj.title || item.form || "OES Form";
  const vendorName = supplierObj.supplierName || item.vendor || item.supplierName || "N/A";
  const exhibitorCode = item.submittedBy || item.exhibitor || item.companyName || item.user?.companyName || "N/A";

  const resolvedFinalAmount = grandTotal > 0 ? grandTotal : (subtotal > 0 ? subtotal : (rate * qty * days) || 0);

  return {
    _id: item._id || item.id || `tx-${idx}`,
    form: formName,
    vendor: vendorName,
    exhibitor: exhibitorCode,
    item: itemDesc,
    startDate: startDateStr,
    endDate: endDateStr,
    days: days,
    qty: qty,
    rate: rate,
    amount: resolvedFinalAmount,
    subtotal: subtotal,
    gst: explicitGst || 0,
    grandTotal: grandTotal > 0 ? grandTotal : resolvedFinalAmount,
    itemsList: parsedItems,
    rawSubmission: item,
  };
};

const generateEmailPreview = (submission, headerUrl = "", footerUrl = "") => {
  if (!submission) return "<p>No submission data available.</p>";

  const metrics = extractSubmissionMetrics(submission);
  const raw = submission.rawSubmission || submission || {};
  const supplier =
    submission.selectedSupplier ||
    raw.selectedSupplier ||
    raw.supplier ||
    raw.supplierDetails ||
    (metrics.vendor && metrics.vendor !== "N/A"
      ? {
          supplierName: metrics.vendor,
          address: "Exhibition Grounds, Bengaluru",
          phoneNumber: "+91 80 6624 6600",
          emails: ["vendor@expoplanner.in"],
        }
      : {});

  const rawUser = raw.user || raw.userId || raw.exhibitorDetails || {};
  const mockUser = {
    companyName: {
      value:
        rawUser.companyName?.value ||
        rawUser.companyName ||
        raw.companyName ||
        metrics.exhibitor ||
        submission.submittedBy ||
        "Exhibitor Company",
    },
    contactSalutation: { value: rawUser.salutation || "" },
    contactFirstName: {
      value:
        rawUser.firstName ||
        rawUser.contactFirstName ||
        raw.contactPerson ||
        raw.name ||
        "",
    },
    contactLastName: { value: rawUser.lastName || rawUser.contactLastName || "" },
    contactName:
      rawUser.contactName ||
      `${rawUser.firstName || raw.contactPerson || ""} ${rawUser.lastName || ""}`.trim() ||
      "Representative",
    mobile: {
      value:
        rawUser.mobile?.value ||
        rawUser.mobile ||
        rawUser.phone ||
        raw.mobile ||
        raw.phone ||
        raw.contactNumber ||
        "N/A",
    },
    contactEmail: {
      value:
        rawUser.contactEmail?.value ||
        rawUser.contactEmail ||
        rawUser.email ||
        raw.contactEmail ||
        raw.email ||
        "N/A",
    },
    stallNo: rawUser.stallNo || raw.stallNo || raw.stallNumber || raw.boothNo || "",
    hallNo: rawUser.hallNo || raw.hallNo || raw.hallNumber || raw.hall || "",
  };

  let bodyHtml = "";

  // 1. Check for Itemized Products / Items Table
  if (metrics.itemsList && metrics.itemsList.length > 0) {
    const rowsHtml = metrics.itemsList
      .map((item, idx) => {
        return `<tr>
          <td>${idx + 1}</td>
          <td><strong>${item.name}</strong>${item.specifications ? `<br/><small style="color:#64748b;">${item.specifications}</small>` : ""}</td>
          <td style="text-align: center;">${item.qty}</td>
          <td style="text-align: center;">${item.days}</td>
          <td style="text-align: right;">₹ ${Number(item.rate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
          <td style="text-align: right; font-weight: 600;">₹ ${Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>`;
      })
      .join("");

    bodyHtml += `<div class="section-title">Requisition Item Breakdown</div>
    <table>
      <thead>
        <tr>
          <th style="width: 5%;">#</th>
          <th style="width: 45%;">Item / Service Description</th>
          <th style="width: 10%; text-align: center;">Qty</th>
          <th style="width: 10%; text-align: center;">Days</th>
          <th style="width: 15%; text-align: right;">Rate (₹)</th>
          <th style="width: 15%; text-align: right;">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
        ${
          metrics.gst > 0
            ? `<tr>
                <td colspan="5" style="text-align: right; font-weight: 600; background-color: #f8fafc;">Subtotal:</td>
                <td style="text-align: right; font-weight: 600; background-color: #f8fafc;">₹ ${Number(metrics.subtotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
              </tr>
              <tr>
                <td colspan="5" style="text-align: right; font-weight: 600; background-color: #f8fafc;">18% GST:</td>
                <td style="text-align: right; font-weight: 600; background-color: #f8fafc;">₹ ${Number(metrics.gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
              </tr>`
            : ""
        }
        <tr class="total-row">
          <td colspan="5" style="text-align: right; font-weight: 700;">Grand Total Amount:</td>
          <td style="text-align: right; color: #15803d; font-size: 1.05rem;">₹ ${Number(metrics.grandTotal || metrics.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>`;
  } else if (metrics.item || metrics.amount || metrics.rate) {
    // Single transaction item breakdown
    const amountVal = Number(metrics.amount || 0);
    const rateVal = Number(metrics.rate || 0);
    const daysVal = Number(metrics.days || 1);
    const qtyVal = Number(metrics.qty || 1);

    bodyHtml += `<div class="section-title">Requisition Details</div>
    <table>
      <tbody>
        <tr>
          <td class="label-cell">Form Name</td>
          <td><strong>${metrics.form || "OES Requisition"}</strong></td>
        </tr>
        <tr>
          <td class="label-cell">Item / Requirement</td>
          <td><strong>${metrics.item || "Standard Form Item"}</strong></td>
        </tr>
        <tr>
          <td class="label-cell">Rental / Service Period</td>
          <td>${metrics.startDate || "Start Date"} to ${metrics.endDate || "End Date"} (${daysVal} Day${daysVal > 1 ? "s" : ""})</td>
        </tr>
        <tr>
          <td class="label-cell">Quantity</td>
          <td>${qtyVal} Unit(s)</td>
        </tr>
        ${rateVal > 0 ? `<tr>
          <td class="label-cell">Unit Rate</td>
          <td>₹ ${rateVal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>` : ""}
        <tr>
          <td class="label-cell">Total Amount</td>
          <td class="price-text" style="font-size: 1.05rem;">₹ ${amountVal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>`;
  }

  // 2. Dynamic Form Responses & Custom Fields (formData / formValues / fields / answers)
  const formDataObj =
    raw.formData ||
    raw.formValues ||
    raw.values ||
    raw.submissionData ||
    raw.fieldsData ||
    {};

  const fieldsList = Array.isArray(raw.fields || raw.formFields || raw.responses || raw.answers)
    ? raw.fields || raw.formFields || raw.responses || raw.answers
    : null;

  const entriesToRender = [];

  if (fieldsList) {
    fieldsList.forEach((f) => {
      const label = f.label || f.name || f.question || f.title;
      let val = f.value ?? f.answer ?? f.val;
      if (label && val !== undefined && val !== null && val !== "") {
        if (typeof val === "object") {
          val = JSON.stringify(val);
        }
        entriesToRender.push({ label, value: String(val) });
      }
    });
  } else if (typeof formDataObj === "object" && Object.keys(formDataObj).length > 0) {
    Object.entries(formDataObj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && typeof v !== "function") {
        let displayVal = typeof v === "object" ? JSON.stringify(v) : String(v);
        const formattedKey = k
          .replace(/([A-Z])/g, " $1")
          .replace(/_/g, " ")
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
        entriesToRender.push({ label: formattedKey, value: displayVal });
      }
    });
  }

  // Excluded keys from general raw scan
  const excludedKeys = new Set([
    "_id", "id", "rawSubmission", "form", "vendor", "exhibitor", "submittedBy",
    "item", "startDate", "endDate", "days", "qty", "rate", "amount", "items",
    "products", "selectedProducts", "selectedSupplier", "supplier", "user", "userId",
    "createdAt", "updatedAt", "__v", "tableData", "lineItems", "formData", "formValues", "values", "fields"
  ]);

  Object.entries(raw).forEach(([k, v]) => {
    if (!excludedKeys.has(k) && v !== undefined && v !== null && v !== "" && typeof v !== "object") {
      const formattedKey = k
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
      if (!entriesToRender.some((e) => e.label.toLowerCase() === formattedKey.toLowerCase())) {
        entriesToRender.push({ label: formattedKey, value: String(v) });
      }
    }
  });

  if (entriesToRender.length > 0) {
    const rowsHtml = entriesToRender
      .map(
        (e) => `<tr>
          <td class="label-cell">${e.label}</td>
          <td>${e.value}</td>
        </tr>`
      )
      .join("");

    bodyHtml += `<div class="section-title">Submitted Form Responses</div>
    <table>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>`;
  }

  // 3. Team Members / Passes List (if applicable)
  const teamList = raw.teamMembers || raw.members || raw.teamMemberList || [];
  if (Array.isArray(teamList) && teamList.length > 0) {
    const teamRows = teamList
      .map(
        (m, idx) => `<tr>
          <td>${idx + 1}</td>
          <td><strong>${m.name || `${m.firstName || ""} ${m.lastName || ""}`.trim() || "Member"}</strong></td>
          <td>${m.designation || m.role || "N/A"}</td>
          <td>${m.mobile || m.phone || "N/A"}</td>
          <td>${m.email || "N/A"}</td>
        </tr>`
      )
      .join("");

    bodyHtml += `<div class="section-title">Submitted Team & Staff Passes</div>
    <table>
      <thead>
        <tr>
          <th style="width: 5%;">#</th>
          <th style="width: 30%;">Full Name</th>
          <th style="width: 25%;">Designation</th>
          <th style="width: 20%;">Mobile</th>
          <th style="width: 20%;">Email</th>
        </tr>
      </thead>
      <tbody>
        ${teamRows}
      </tbody>
    </table>`;
  }

  // 4. Attachments / Uploaded Files (if applicable)
  const filesList = raw.files || raw.documents || raw.attachments || (raw.fileUrl ? [raw.fileUrl] : []);
  if (Array.isArray(filesList) && filesList.length > 0) {
    const filesHtml = filesList
      .map((f, idx) => {
        const url = typeof f === "string" ? f : f.url || f.fileUrl;
        const name = typeof f === "string" ? `Document ${idx + 1}` : f.name || f.fileName || `Attachment ${idx + 1}`;
        return `<li><a href="${url}" target="_blank" style="color: #2563eb; text-decoration: underline; font-weight: 600;">${name}</a></li>`;
      })
      .join("");

    bodyHtml += `<div class="section-title">Uploaded Attachments</div>
    <ul style="margin: 8px 0; padding-left: 20px;">
      ${filesHtml}
    </ul>`;
  }

  return oesTemplate(
    mockUser,
    bodyHtml,
    supplier,
    submission.form || submission.name || "OES Requisition",
    headerUrl,
    footerUrl
  );
};

const buildVendorTransactions = () => {
  const baseRecords = [
    {
      _id: "tx-001",
      form: "5.6 AUDIO/VISUAL EQUIPMENT",
      vendor: "Dreams Events & Services",
      exhibitor: "IMTEX20252304",
      item: 'LED TV 40" WITH STAND OR WALL MOUNT',
      startDate: "22-01-2025",
      endDate: "28-01-2025",
      days: 7,
      qty: 1,
      rate: 2800,
      amount: 19600,
    },
    {
      _id: "tx-002",
      form: "5.6 AUDIO/VISUAL EQUIPMENT",
      vendor: "Dreams Events & Services",
      exhibitor: "IMTEX20252304",
      item: 'LED TV 50" WITH STAND OR WALL MOUNT',
      startDate: "22-01-2025",
      endDate: "28-01-2025",
      days: 7,
      qty: 1,
      rate: 2750,
      amount: 19250,
    },
    {
      _id: "tx-003",
      form: "4.1 Stand Possession",
      vendor: "Aesthetic Garden",
      exhibitor: "PMX202604",
      item: "EXHIBITION PLANTS & LANDSCAPING SET",
      startDate: "21-01-2025",
      endDate: "28-01-2025",
      days: 7,
      qty: 2,
      rate: 3500,
      amount: 49000,
    },
    {
      _id: "tx-004",
      form: "5.1 Electrical Services",
      vendor: "Indian Machine Tool Manufacturers' Association | IMTMA",
      exhibitor: "PMX2026101",
      item: "3-PHASE POWER CONNECTION 15 KW",
      startDate: "20-01-2025",
      endDate: "28-01-2025",
      days: 8,
      qty: 1,
      rate: 12500,
      amount: 100000,
    },
    {
      _id: "tx-005",
      form: "5.2 Booth Construction Approval",
      vendor: "Wadhwa Designs & Exhibits Pvt. Ltd",
      exhibitor: "PMX202640",
      item: "CUSTOM WOODEN STALL STRUCTURE 36 SQM",
      startDate: "18-01-2025",
      endDate: "28-01-2025",
      days: 10,
      qty: 1,
      rate: 45000,
      amount: 450000,
    },
    {
      _id: "tx-006",
      form: "5.3 Extra Furniture Rental",
      vendor: "P S Bedi & Company Pvt. Ltd.",
      exhibitor: "PMX202672",
      item: "EXECUTIVE CONFERENCE CHAIRS & LOCKABLE COUNTER",
      startDate: "22-01-2025",
      endDate: "28-01-2025",
      days: 6,
      qty: 5,
      rate: 1800,
      amount: 54000,
    },
    {
      _id: "tx-007",
      form: "5.4 Freight Logistics",
      vendor: "R.E. Rogers India Pvt. Ltd.",
      exhibitor: "PMX202680",
      item: "HEAVY MACHINERY UNLOADING & FORKLIFT SERVICE",
      startDate: "19-01-2025",
      endDate: "28-01-2025",
      days: 9,
      qty: 2,
      rate: 24000,
      amount: 432000,
    },
  ];

  const formsList = [
    "4.1 Stand Possession",
    "5.6 AUDIO/VISUAL EQUIPMENT",
    "5.1 Electrical Services",
    "5.2 Booth Construction Approval",
    "5.3 Extra Furniture Rental",
    "5.4 Freight Logistics",
    "5.5 Telecommunication & Wi-Fi",
  ];

  const vendorsList = [
    "Dreams Events & Services",
    "Aesthetic Garden",
    "Indian Machine Tool Manufacturers' Association | IMTMA",
    "Wadhwa Designs & Exhibits Pvt. Ltd",
    "P S Bedi & Company Pvt. Ltd.",
    "R.E. Rogers India Pvt. Ltd.",
    "Apex Expo Decor",
    "Metro Utilities",
    "Quantum Media",
    "Precision Structures",
    "Global Trade Logistics",
  ];

  const exhibitorsList = [
    "IMTEX20252304",
    "PMX202604",
    "PMX2026101",
    "PMX202640",
    "PMX202672",
    "PMX202680",
    "PMX202654",
    "PMX202691",
    "PMX2026149",
    "PMX202622",
    "PMX202615",
    "PMX202633",
    "PMX202688",
    "PMX202612",
  ];

  const itemsList = [
    'LED TV 40" WITH STAND OR WALL MOUNT',
    'LED TV 50" WITH STAND OR WALL MOUNT',
    'PA SYSTEM WITH 2 WIRELESS MICS',
    'EXHIBITION PLANTS & LANDSCAPING SET',
    'VIP FLORAL ARRANGEMENT & PALM POTS',
    '3-PHASE POWER CONNECTION 15 KW',
    'SINGLE PHASE POWER POINT 5AMP',
    'CUSTOM WOODEN STALL STRUCTURE 36 SQM',
    'HIGH-GLOSS OCTANORM SYSTEM BOOTH 18 SQM',
    'EXECUTIVE CONFERENCE CHAIRS & LOCKABLE COUNTER',
    'ROUND GLASS MEETING TABLE WITH 4 CHAIRS',
    'HEAVY MACHINERY UNLOADING & FORKLIFT SERVICE',
  ];

  const result = [...baseRecords];
  let currentTotal = baseRecords.reduce((acc, r) => acc + r.amount, 0); 
  const targetTotal = 1165310;
  const remainingNeeded = targetTotal - currentTotal;

  for (let i = 8; i <= 50; i++) {
    const form = formsList[i % formsList.length];
    const vendor = vendorsList[i % vendorsList.length];
    const exhibitor = exhibitorsList[i % exhibitorsList.length];
    const item = itemsList[i % itemsList.length];
    const days = 5 + (i % 4);
    const qty = 1 + (i % 2);

    let amount = 0;
    if (i === 50) {
      const sumSoFar = result.reduce((acc, r) => acc + r.amount, 0);
      amount = Math.max(1200, targetTotal - sumSoFar);
    } else {
      amount = Math.round(remainingNeeded / 43 + (i * 37) % 650);
    }

    const rate = Math.round(amount / (days * qty));

    result.push({
      _id: `tx-${i < 10 ? "00" + i : "0" + i}`,
      form: form,
      vendor: vendor,
      exhibitor: exhibitor,
      item: item,
      startDate: `2${i % 4 + 0}-01-2025`,
      endDate: "28-01-2025",
      days: days,
      qty: qty,
      rate: rate,
      amount: amount,
    });
  }

  return result;
};

const DEFAULT_TRANSACTIONS = buildVendorTransactions();

// Helper to determine Form category badge styling
const getFormBadgeStyle = (formName) => {
  const str = (formName || "").toLowerCase();
  if (str.includes("audio") || str.includes("visual") || str.includes("5.6")) {
    return { bgcolor: "#dcfce7", color: "#15803d", label: formName };
  }
  if (str.includes("stand") || str.includes("possession") || str.includes("4.1")) {
    return { bgcolor: "#ccfbf1", color: "#0f766e", label: formName };
  }
  if (str.includes("electrical") || str.includes("power") || str.includes("5.1")) {
    return { bgcolor: "#dbeafe", color: "#1d4ed8", label: formName };
  }
  if (str.includes("booth") || str.includes("construction") || str.includes("5.2")) {
    return { bgcolor: "#f3e8ff", color: "#6b21a8", label: formName };
  }
  if (str.includes("furniture") || str.includes("5.3")) {
    return { bgcolor: "#fef3c7", color: "#b45309", label: formName };
  }
  if (str.includes("freight") || str.includes("logistics") || str.includes("5.4")) {
    return { bgcolor: "#ffe4e6", color: "#be123c", label: formName };
  }
  return { bgcolor: "#e2e8f0", color: "#334155", label: formName };
};

// --- MAIN REACT COMPONENT ---
const OesExplorer = () => {
  // Fetch live API data for OES submissions, form configurations, and registered suppliers
  const { data: adminOesData, isLoading: isAdminLoading, isError: isAdminError, error: adminError } = useApiEp(
    `/admin/get-all-oes-submit`
  );
  const { data: userOesData, isLoading: isUserLoading } = useApiEp(
    `/user/get-oes-submissions`
  );
  const { data: formItemsData } = useApiEp(`/admin/get-form-items`);
  const { data: suppliersData } = useApiEp(`/admin/get-suppliers`);
  const { data: headerFooterData } = useEmailHeaderFooter();

  const headerUrl = headerFooterData?.headerUrl || "";
  const footerUrl = headerFooterData?.footerUrl || "";

  const isLoading = isAdminLoading && isUserLoading;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFormFilter, setSelectedFormFilter] = useState("ALL");
  const [selectedVendorFilter, setSelectedVendorFilter] = useState("ALL");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("ALL");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Raw API response prioritization
  const rawSubmissionsData = adminOesData || userOesData;

  // Transform live API data into normalized transaction records
  const allTransactions = useMemo(() => {
    let list = [];
    if (Array.isArray(rawSubmissionsData)) {
      list = rawSubmissionsData;
    } else if (rawSubmissionsData && Array.isArray(rawSubmissionsData.data)) {
      list = rawSubmissionsData.data;
    } else if (rawSubmissionsData && Array.isArray(rawSubmissionsData.submissions)) {
      list = rawSubmissionsData.submissions;
    } else if (rawSubmissionsData && Array.isArray(rawSubmissionsData.result)) {
      list = rawSubmissionsData.result;
    } else if (rawSubmissionsData && Array.isArray(rawSubmissionsData.items)) {
      list = rawSubmissionsData.items;
    }

    if (list.length === 0) {
      list = DEFAULT_TRANSACTIONS;
    }

    return list.map((item, idx) => extractSubmissionMetrics(item, idx));
  }, [rawSubmissionsData]);

  // Dynamic Options for Form Filter Dropdown from API
  const formOptions = useMemo(() => {
    const set = new Set();
    // Include form items from API configuration if available
    if (Array.isArray(formItemsData)) {
      formItemsData.forEach((f) => {
        if (f.name || f.title || f.formName) set.add(f.name || f.title || f.formName);
      });
    }
    allTransactions.forEach((tx) => {
      if (tx.form && tx.form !== "N/A") set.add(tx.form);
    });
    return Array.from(set).sort();
  }, [allTransactions, formItemsData]);

  // Dynamic Options for Vendor Filter Dropdown from API
  const vendorOptions = useMemo(() => {
    const set = new Set();
    if (Array.isArray(suppliersData)) {
      suppliersData.forEach((s) => {
        if (s.supplierName || s.name) set.add(s.supplierName || s.name);
      });
    }
    allTransactions.forEach((tx) => {
      if (tx.vendor && tx.vendor !== "N/A") set.add(tx.vendor);
    });
    return Array.from(set).sort();
  }, [allTransactions, suppliersData]);

  // Status Mapping for Form Items from /admin/get-form-items
  const formStatusMap = useMemo(() => {
    const map = {};
    if (Array.isArray(formItemsData)) {
      formItemsData.forEach((item) => {
        const name = item.name || item.title || item.formName;
        if (name) {
          map[name] = item.disabled ? "Inactive" : "Active";
        }
      });
    }
    return map;
  }, [formItemsData]);

  // Live Filtered Transactions based on Search & Select Inputs
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter((tx) => {
      const matchForm =
        selectedFormFilter === "ALL" ||
        selectedFormFilter === "All Forms" ||
        tx.form === selectedFormFilter;

      const matchVendor =
        selectedVendorFilter === "ALL" ||
        selectedVendorFilter === "All Vendors" ||
        tx.vendor === selectedVendorFilter;

      const txStatus = formStatusMap[tx.form] || "Active";
      const matchStatus =
        selectedStatusFilter === "ALL" ||
        txStatus === selectedStatusFilter;

      const query = searchQuery.trim().toLowerCase();
      const matchQuery =
        !query ||
        tx.exhibitor?.toLowerCase().includes(query) ||
        tx.vendor?.toLowerCase().includes(query) ||
        tx.item?.toLowerCase().includes(query) ||
        tx.form?.toLowerCase().includes(query);

      return matchForm && matchVendor && matchStatus && matchQuery;
    });
  }, [allTransactions, selectedFormFilter, selectedVendorFilter, selectedStatusFilter, formStatusMap, searchQuery]);

  // Paginated Table Records
  const paginatedTransactions = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredTransactions.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredTransactions, page]);

  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage) || 1;

  // DYNAMIC STAT METRICS DERIVED STRICTLY FROM LIVE API DATA
  const { activeFormsCount, inactiveFormsCount } = useMemo(() => {
    if (Array.isArray(formItemsData) && formItemsData.length > 0) {
      const active = formItemsData.filter((item) => !item.disabled).length;
      const inactive = formItemsData.filter((item) => item.disabled === true).length;
      return { activeFormsCount: active, inactiveFormsCount: inactive };
    }
    const uniqueForms = new Set(allTransactions.map((tx) => tx.form));
    return { activeFormsCount: uniqueForms.size, inactiveFormsCount: 0 };
  }, [formItemsData, allTransactions]);

  const vendorsCount = useMemo(() => {
    if (Array.isArray(suppliersData) && suppliersData.length > 0) {
      return suppliersData.length;
    }
    const uniqueVendors = new Set(allTransactions.map((tx) => tx.vendor));
    return uniqueVendors.size;
  }, [suppliersData, allTransactions]);

  const exhibitorsCount = useMemo(() => {
    const uniqueExhibitors = new Set(allTransactions.map((tx) => tx.exhibitor).filter((e) => e && e !== "N/A"));
    return uniqueExhibitors.size;
  }, [allTransactions]);

  const totalAmountSum = useMemo(() => {
    return filteredTransactions.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
  }, [filteredTransactions]);

  const hasActiveFilters =
    selectedFormFilter !== "ALL" ||
    selectedVendorFilter !== "ALL" ||
    selectedStatusFilter !== "ALL" ||
    searchQuery.trim() !== "";

  const handleResetFilters = () => {
    setSelectedFormFilter("ALL");
    setSelectedVendorFilter("ALL");
    setSelectedStatusFilter("ALL");
    setSearchQuery("");
    setPage(1);
  };

  const handleExportToExcel = () => {
    const exportData = filteredTransactions.map((tx) => ({
      "Form": tx.form,
      "Vendor": tx.vendor,
      "Exhibitor": tx.exhibitor,
      "Item": tx.item,
      "Start Date": tx.startDate,
      "End Date": tx.endDate,
      "Days": tx.days,
      "Quantity": tx.qty,
      "Rate (INR)": tx.rate,
      "Amount (INR)": tx.amount,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendor Transactions");
    XLSX.writeFile(workbook, "Vendor_Transaction_Details.xlsx");
  };

  const handleOpenDialog = (transaction) => {
    const raw = transaction.rawSubmission || transaction || {};
    const metrics = extractSubmissionMetrics(raw);
    setSelectedSubmission({
      ...metrics,
      ...raw,
      ...transaction,
      form: transaction.form || metrics.form || raw.formName || raw.name || "OES Form",
      name: transaction.form || metrics.form || raw.name || "OES Form",
      submittedBy: transaction.exhibitor || metrics.exhibitor || raw.submittedBy || raw.companyName,
      selectedSupplier: raw.selectedSupplier || raw.supplier || raw.supplierDetails || {
        supplierName: transaction.vendor || metrics.vendor,
        address: "BIEC, Exhibition Grounds, Bengaluru",
        phoneNumber: "+91 80 6624 6600",
        emails: ["vendor@expoplanner.in"],
      },
      rawSubmission: raw,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSubmission(null);
  };

  if (isLoading) return <MuPb />;

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 3, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>
        
        {/* PAGE HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center">
            <BackBtn />
            <Typography variant="h5" ml={2} fontWeight="700" color="#0f172a">
              OES Submissions & Vendor Transactions
            </Typography>
            <Chip
              label="Live Sync"
              size="small"
              sx={{
                ml: 2,
                bgcolor: "#dcfce7",
                color: "#15803d",
                fontWeight: 700,
                fontSize: "0.7rem",
                borderRadius: "6px",
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: "14px !important", color: "#475569" }} />}
              label="IMTEX 2025 / PMTX 2026"
              variant="outlined"
              sx={{
                borderRadius: "8px",
                borderColor: "#cbd5e1",
                bgcolor: "#ffffff",
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            />
          </Box>
        </Box>

        {/* 1. DYNAMIC TOP STAT CARDS ROW */}
        <Grid container spacing={2.5} mb={3}>
          {/* Card 1: ACTIVE FORMS */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "8px",
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(15,23,42,0.08)" },
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    bgcolor: "#2563eb",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                    boxShadow: "0 3px 8px rgba(37,99,235,0.25)",
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", display: "block" }}
                  >
                    ACTIVE FORMS
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.1, mt: 0.2 }}>
                    {activeFormsCount}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, mt: 0.3, display: "block" }}>
                    {inactiveFormsCount > 0
                      ? `${inactiveFormsCount} Inactive Form${inactiveFormsCount > 1 ? "s" : ""}`
                      : "All Forms Active"}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Card 2: VENDORS */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "8px",
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(15,23,42,0.08)" },
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    bgcolor: "#059669",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                    boxShadow: "0 3px 8px rgba(5,150,105,0.25)",
                  }}
                >
                  <StorefrontIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", display: "block" }}
                  >
                    VENDORS
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.1, mt: 0.2 }}>
                    {vendorsCount}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Card 3: EXHIBITORS */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "8px",
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(15,23,42,0.08)" },
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    bgcolor: "#7c3aed",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                    boxShadow: "0 3px 8px rgba(124,58,237,0.25)",
                  }}
                >
                  <GroupIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", display: "block" }}
                  >
                    EXHIBITORS
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.1, mt: 0.2 }}>
                    {exhibitorsCount}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Card 4: TOTAL AMOUNT */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "8px",
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "100%",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(15,23,42,0.08)" },
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    bgcolor: "#d97706",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    mr: 2,
                    boxShadow: "0 3px 8px rgba(217,119,6,0.25)",
                  }}
                >
                  Rs
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", fontWeight: 700, letterSpacing: "0.5px", display: "block" }}
                  >
                    TOTAL AMOUNT
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.1, mt: 0.2, whiteSpace: "nowrap" }}
                  >
                    ₹ {totalAmountSum.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* 2. FILTER & EXPORT DATA CARD */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "8px",
            bgcolor: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <FilterListIcon sx={{ color: "#2563eb", mr: 1, fontSize: 22 }} />
              <Typography variant="h6" fontWeight="700" color="#0f172a" fontSize="1rem">
                Filter & Export Data
              </Typography>
            </Box>

            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilters}
                sx={{
                  color: "#ef4444",
                  fontWeight: 600,
                  fontSize: "0.775rem",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#fef2f2" },
                }}
              >
                Reset Filters
              </Button>
            )}
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 700, display: "block", mb: 0.6 }}
              >
                Search Query
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search exhibitor, vendor, or item..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "#ffffff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "#cbd5e1" },
                  },
                }}
              />
            </Grid>

            {/* Select Form Dropdown */}
            <Grid item xs={12} md={2.7}>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 700, display: "flex", alignItems: "center", mb: 0.6 }}
              >
                <DescriptionIcon sx={{ fontSize: 15, mr: 0.5, color: "#64748b" }} />
                Select Form
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedFormFilter}
                  onChange={(e) => {
                    setSelectedFormFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                  }}
                >
                  <MenuItem value="ALL">All Forms</MenuItem>
                  {formOptions.map((form) => {
                    const status = formStatusMap[form] || "Active";
                    return (
                      <MenuItem
                        key={form}
                        value={form}
                        sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.75 }}
                      >
                        <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
                          {form}
                        </Typography>
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.625rem",
                            fontWeight: 700,
                            bgcolor: status === "Active" ? "#dcfce7" : "#fee2e2",
                            color: status === "Active" ? "#166534" : "#991b1b",
                            borderRadius: "4px",
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Select Vendor Dropdown */}
            <Grid item xs={12} md={2.3}>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 700, display: "flex", alignItems: "center", mb: 0.6 }}
              >
                <StorefrontIcon sx={{ fontSize: 15, mr: 0.5, color: "#64748b" }} />
                Select Vendor
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedVendorFilter}
                  onChange={(e) => {
                    setSelectedVendorFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                  }}
                >
                  <MenuItem value="ALL">All Vendors</MenuItem>
                  {vendorOptions.map((vendor) => (
                    <MenuItem key={vendor} value={vendor}>
                      {vendor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Form Status Filter Dropdown */}
            <Grid item xs={12} md={2}>
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontWeight: 700, display: "block", mb: 0.6 }}
              >
                Form Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedStatusFilter}
                  onChange={(e) => {
                    setSelectedStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                  }}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  <MenuItem value="Active">Active Forms Only</MenuItem>
                  <MenuItem value="Inactive">Inactive Forms Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Export Button */}
            <Grid item xs={12} md={2} display="flex" alignItems="flex-end">
              <Button
                fullWidth
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToExcel}
                sx={{
                  bgcolor: "#0d9488",
                  "&:hover": { bgcolor: "#0f766e" },
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  px: 2.5,
                  height: 40,
                  width: { xs: "100%", md: "auto" },
                  boxShadow: "0 2px 6px rgba(13,148,136,0.2)",
                  whiteSpace: "nowrap",
                }}
              >
                Export to Excel
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* 3. VENDOR TRANSACTION DETAILS TABLE CARD */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "8px",
            bgcolor: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            px={3}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom="1px solid #e2e8f0"
            bgcolor="#ffffff"
          >
            <Box display="flex" alignItems="center">
              <TableChartIcon sx={{ color: "#0f172a", mr: 1, fontSize: 22 }} />
              <Typography variant="h6" fontWeight="700" color="#0f172a" fontSize="1rem">
                Vendor Transaction Details
              </Typography>
            </Box>
            <Chip
              label={`Showing ${paginatedTransactions.length} of ${filteredTransactions.length} records`}
              size="small"
              sx={{
                bgcolor: "#f1f5f9",
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.775rem",
                borderRadius: "6px",
              }}
            />
          </Box>

          <TableContainer component={Paper} elevation={0}>
            <MuiTable>
              <MuiTableHead sx={{ bgcolor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                <MuiTableRow>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }}>
                    FORM
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }}>
                    VENDOR
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }}>
                    EXHIBITOR
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }}>
                    ITEM
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="center">
                    START DATE
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="center">
                    END DATE
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="center">
                    DAYS
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="center">
                    QTY
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="right">
                    RATE (₹)
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="right">
                    AMOUNT (₹)
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 700, fontSize: "0.725rem", py: 1.5, letterSpacing: "0.5px" }} align="center">
                    EMAIL PREVIEW
                  </TableCell>
                </MuiTableRow>
              </MuiTableHead>
              <MuiTableBody>
                {paginatedTransactions.length > 0 ? (
                  paginatedTransactions.map((row) => {
                    const formStyle = getFormBadgeStyle(row.form);

                    return (
                      <MuiTableRow
                        key={row._id}
                        hover
                        sx={{ "&:hover": { bgcolor: "#f8fafc" }, transition: "background-color 0.15s ease" }}
                      >
                        {/* Form Category Badge */}
                        <TableCell sx={{ py: 1.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Chip
                              label={formStyle.label}
                              size="small"
                              sx={{
                                bgcolor: formStyle.bgcolor,
                                color: formStyle.color,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                borderRadius: "6px",
                                height: 24,
                              }}
                            />
                            <Chip
                              label={formStatusMap[row.form] || "Active"}
                              size="small"
                              sx={{
                                bgcolor: formStatusMap[row.form] === "Inactive" ? "#fee2e2" : "#dcfce7",
                                color: formStatusMap[row.form] === "Inactive" ? "#991b1b" : "#166534",
                                fontWeight: 700,
                                fontSize: "0.625rem",
                                borderRadius: "4px",
                                height: 18,
                              }}
                            />
                          </Box>
                        </TableCell>

                        {/* Vendor Link */}
                        <TableCell sx={{ py: 1.75 }}>
                          <Tooltip title="Click to view full vendor requisition" arrow placement="top">
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#2563eb",
                                fontWeight: 600,
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() => handleOpenDialog(row)}
                            >
                              {row.vendor}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Exhibitor ID Badge */}
                        <TableCell sx={{ py: 1.75 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace, monospace",
                              bgcolor: "#f1f5f9",
                              color: "#334155",
                              fontWeight: 700,
                              px: 1,
                              py: 0.4,
                              borderRadius: "6px",
                              fontSize: "0.775rem",
                              display: "inline-block",
                            }}
                          >
                            {row.exhibitor}
                          </Typography>
                        </TableCell>

                        {/* Item Description */}
                        <TableCell sx={{ py: 1.75 }}>
                          <Typography
                            variant="body2"
                            color="#0f172a"
                            fontWeight="600"
                            fontSize="0.825rem"
                          >
                            {row.item}
                          </Typography>
                        </TableCell>

                        {/* Start Date */}
                        <TableCell sx={{ py: 1.75 }} align="center">
                          <Typography variant="body2" color="#475569" fontSize="0.825rem">
                            {row.startDate}
                          </Typography>
                        </TableCell>

                        {/* End Date */}
                        <TableCell sx={{ py: 1.75 }} align="center">
                          <Typography variant="body2" color="#475569" fontSize="0.825rem">
                            {row.endDate}
                          </Typography>
                        </TableCell>

                        {/* Days Indicator */}
                        <TableCell sx={{ py: 1.75 }} align="center">
                          <Box
                            sx={{
                              bgcolor: "#fef3c7",
                              color: "#92400e",
                              fontWeight: 700,
                              borderRadius: "6px",
                              px: 1,
                              py: 0.25,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                            }}
                          >
                            {row.days}
                          </Box>
                        </TableCell>

                        {/* Qty */}
                        <TableCell sx={{ py: 1.75 }} align="center">
                          <Typography variant="body2" color="#334155" fontWeight="600">
                            {row.qty}
                          </Typography>
                        </TableCell>

                        {/* Rate */}
                        <TableCell sx={{ py: 1.75 }} align="right">
                          <Typography variant="body2" color="#334155" fontWeight="600" sx={{ fontFamily: "monospace, monospace" }}>
                            {Number(row.rate).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </TableCell>

                        {/* Amount */}
                        <TableCell sx={{ py: 1.75 }} align="right">
                          <Typography variant="body2" color="#0f172a" fontWeight="700" sx={{ fontFamily: "monospace, monospace" }}>
                            {Number(row.amount).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </TableCell>

                        {/* Action Preview Button */}
                        <TableCell sx={{ py: 1.75 }} align="center">
                          <Tooltip title="Preview Generated Email" arrow placement="top">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon sx={{ fontSize: "15px !important" }} />}
                              onClick={() => handleOpenDialog(row)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                py: 0.35,
                                px: 1.25,
                              }}
                            >
                              Preview
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </MuiTableRow>
                    );
                  })
                ) : (
                  <MuiTableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="#64748b" fontWeight="600">
                        No vendor transactions match your active search or filters.
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleResetFilters}
                        sx={{ mt: 1, textTransform: "none", color: "#2563eb" }}
                      >
                        Clear Filters
                      </Button>
                    </TableCell>
                  </MuiTableRow>
                )}
              </MuiTableBody>
            </MuiTable>
          </TableContainer>

          {/* TABLE PAGINATION FOOTER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 3,
              py: 1.75,
              borderTop: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Typography variant="caption" color="#64748b" fontWeight="600">
              Page {page} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              color="primary"
              size="small"
              shape="rounded"
            />
          </Box>
        </Card>

        {/* Email & Details Preview Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Typography variant="h6" fontWeight="700" color="#0f172a" fontSize="1.05rem">
              Vendor Requisition Details - {selectedSubmission?.vendor}
            </Typography>
            <IconButton size="small" onClick={handleCloseDialog} sx={{ color: "#64748b" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: "#f8fafc", p: 3 }}>
            {selectedSubmission ? (
              <Box
                sx={{ width: "100%", display: "flex", justifyContent: "center" }}
                dangerouslySetInnerHTML={{
                  __html: generateEmailPreview(selectedSubmission, headerUrl, footerUrl),
                }}
              />
            ) : (
              <MuPb />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
            <Button
              onClick={handleCloseDialog}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default OesExplorer;
